import { Box, IconButton, Grid, Typography } from '@mui/material';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import AppCard from '@/components/appcard/AppCard';
import NewWcnTable, { DataTableColumn, KeyValuePair } from '@/components/datatable/NewWcnTable.tsx';
import { FCSecurePage } from '@/components/shared/navigation/securePage';

import BaseSelector from '@/components/selectors/BaseSelector.tsx';
import DateSelector from '@/components/selectors/DateSelector';
import TimeSelector from '@/components/selectors/TimeSelector';
import WcnButton from '@/components/shared/WcnButton';
import TabSelector, { TabItem } from '@/components/shared/TabSelector';
import { useSettingsStore } from '@core/store';
import StatusTag, { HealthStatus, ShopStatus } from '@/components/shared/Tags.tsx';
import WcnApexchart from '@/components/charts/WcnApexchart';
import EmailInput from '@/components/shared/EmailInput';
import RadioItemGroup from '@/components/shared/RadioItemGroup.tsx';
import WcnCheckbox from '@/components/shared/WcnCheckbox';
import ToggleButton from '@/components/shared/ToggleButton';

// Import mock data
import { WcnTextField } from '@/components';
import { equalsIgnoreCase } from '@core/utils';

const BUTTON_ROWS: { label: string; disabled?: boolean; isProgress?: boolean }[] = [
    { label: 'DEFAULT' },
    { label: 'DISABLED', disabled: true },
    { label: 'PROGRESS', isProgress: true }
];

const SELECTOR_TABS: TabItem[] = [
    { key: 'No Entry', label: 'No Entry' },
    { key: 'Selected', label: 'Selected' },
    { key: 'Disabled', label: 'Disabled' },
    { key: 'Searchable', label: 'Searchable' },
    { key: 'Large List', label: 'Large List' },
    { key: 'Searchable Large List', label: 'Searchable Large List' },
    { key: 'Error', label: 'Error' },
    { key: 'Warning', label: 'Warning' }
];
const CALENDAR_TABS: TabItem[] = [
    { key: 'No Entry', label: 'No Entry' },
    { key: 'Selected', label: 'Selected' },
    { key: 'Disabled', label: 'Disabled' }
];

const LARGE_LIST_ITEMS = Array.from({ length: 500000 }, (_, i) => ({
    value: `item-${i + 1}`,
    text: `Item ${String(i + 1).padStart(2, '0')}`
}));

const SELECTION_OPTIONS = [
    { label: 'Option A', value: 'a' },
    { label: 'Option B', value: 'b' },
    { label: 'Option C', value: 'c' }
] as const;

const DesignElements: FCSecurePage = () => {
    const { darkMode, isMobile } = useSettingsStore();

    // Demo states for single selector
    const [singleEmpty, setSingleEmpty] = useState<string | null>(null);
    const [singleSelected, setSingleSelected] = useState<string | null>('option-a');
    const [singleDisabled] = useState<string | null>('option-a');
    const [singleSearchable, setSingleSearchable] = useState<string | null>(null);
    const [singleLargeList, setSingleLargeList] = useState<string | null>('item-200000');

    // Demo states for multi selector
    const [multiEmpty, setMultiEmpty] = useState<string[]>([]);
    const [multiSelected, setMultiSelected] = useState<string[]>(['option-a', 'option-c']);
    const [multiDisabled] = useState<string[]>(['option-a', 'option-c']);
    const [multiSearchable, setMultiSearchable] = useState<string[]>([]);
    const [multiLargeList, setMultiLargeList] = useState<string | null>('item-200000');
    const [emails, setEmails] = useState<string[]>([
        'name1.surname1@organization.com',
        'john.doe@wanzl.com',
        'jane.smith@wanzl.com'
    ]);

    const [shelterFillLevelData, setShelterFillLevelData] = useState<any[]>([]);
    const [shelterThresholdData, setShelterThresholdData] = useState<any[]>([]);
    const [gatesOpeningsData, setGatesOpeningsData] = useState<any[]>([]);
    const [hybridLocDailyData, setHybridLocDailyData] = useState<any[]>([]);
    const [hybridLocDistributionData, setHybridLocDistributionData] = useState<any[]>([]);
    const [users, setUsers] = useState<{ eMail: string }[]>([]);

    useEffect(() => {
        Promise.all([
            import('../../../mock/data/statistics/shelterfilllevel_GW-BERLIN-001_TS-MAIN_GET_200.json'),
            import('../../../mock/data/trolleyshelter/GW-BERLIN-001_TS-Main_threshold_GET_200.json'),
            import('../../../mock/data/statistics/wanzlGates_openings_GET_200.json'),
            import('../../../mock/data/hybridloc/usage_daily_shop-001_GET_200.json'),
            import('../../../mock/data/hybridloc/usage_distribution_GET_200.json'),
            import('../../../mock/data/user/getAllWithoutSuperAdminsForCurentOrganisation_GET_200.json'),
            import('../../../mock/data/fastlaner/trolley-data_GET_200.json')
        ]).then(([shelterFill, shelterThreshold, gatesOpenings, hybridDaily, hybridDist, user, trolleyData]) => {
            setShelterFillLevelData(shelterFill.default);
            setShelterThresholdData(shelterThreshold.default);
            setGatesOpeningsData(gatesOpenings.default);
            setHybridLocDailyData(hybridDaily.default);
            setHybridLocDistributionData(hybridDist.default);
            setUsers(user.default);
            settrolley(trolleyData.default.data);
        });
    }, []);

    // Tab states
    const [activeTab, setActiveTab] = useState(0);
    const [singleSelectorTab, setSingleSelectorTab] = useState(0);
    const [multiSelectorTab, setMultiSelectorTab] = useState(0);
    const [calendarTab, setCalendarTab] = useState(0);

    // Calendar demo states
    const [calendarEmpty, setCalendarEmpty] = useState<string | null>(null);
    const [calendarSelected, setCalendarSelected] = useState<string | null>('2026-03-15');

    // Time selector demo states
    const [timeSelectorTab, setTimeSelectorTab] = useState(0);
    const [timeEmpty, setTimeEmpty] = useState('');
    const [timeSelected, setTimeSelected] = useState('09:00');

    // Selection Item states
    const [radioSelection, setRadioSelection] = useState<string>('b');
    const [actionsDisabled, setActionsDisabled] = useState(false);
    const [checkboxSelected, setCheckboxSelected] = useState(true);
    const [checkboxSelectable, setCheckboxSelectable] = useState(false);
    const [toggleButtonSelected, setToggleButtonSelected] = useState(false);
    const [toggleButton2Selected, setToggleButton2Selected] = useState(false);

    const userEmails = users.map((u) => u.eMail);
    const isUnknowEmail = useCallback(
        (email: string): string => {
            if (!userEmails.some((e) => equalsIgnoreCase(e, email))) return 'validation.noAccount';

            return '';
        },
        [users]
    );

    useEffect(() => {
        setSingleEmpty(null);
        setSingleSelected('option-a');
        setSingleSearchable(null);
    }, [singleSelectorTab]);

    useEffect(() => {
        setMultiEmpty([]);
        setMultiSelected(['option-a', 'option-c']);
        setMultiSearchable([]);
    }, [multiSelectorTab]);

    useEffect(() => {
        setCalendarEmpty(null);
        setCalendarSelected('2026-03-15');
    }, [calendarTab]);

    useEffect(() => {
        setTimeEmpty('');
        setTimeSelected('09:00');
    }, [timeSelectorTab]);

    // BaseSelector uses { value, text } shape
    const demoItems = [
        { value: 'option-a', text: 'Option A' },
        { value: 'option-b', text: 'Option B' },
        { value: 'option-c', text: 'Option C' },
        { value: 'option-d', text: 'Option D' },
        { value: 'option-e', text: 'Option E' }
    ];

    const [trolley, settrolley] = useState<any>([]);
    const [selectedTrolleys, setSelectedTrolleys] = useState<any[]>([]);

    const columns: DataTableColumn[] = useMemo(
        () => [
            { text: 'Group Name', value: 'groupName', sortable: true, width: 250 },
            { text: 'Custom Group Name', value: 'customGroupName', sortable: true, width: 250 }
        ],
        []
    );

    const columnsWithActions: DataTableColumn[] = useMemo(
        () => [
            ...columns,
            {
                text: 'Health Status',
                value: 'healthStatus',
                width: 300,
                multiLine: true,
                multiLineValue: (trolley): KeyValuePair[] =>
                    Object.entries(trolley.trolleys[0].healthStatus).map(([key, value]) => ({
                        key,
                        label: key,
                        value: String(value ?? '')
                    }))
            },
            {
                text: 'Expected App Versions',
                value: 'expectedAppVersions',
                width: 300,
                multiLine: true,
                multiLineValue: (trolley): KeyValuePair[] => trolley.expectedAppVersions
            },
            {
                text: 'Actions',
                value: 'actions',
                width: 120,
                align: 'center',
                sortable: false,
                isAction: true,
                render: () => (
                    <Box>
                        <IconButton size="small">
                            <i className="bi bi-pencil" />
                        </IconButton>
                        <IconButton size="small">
                            <i className="bi bi-trash3" />
                        </IconButton>
                    </Box>
                )
            }
        ],
        [columns]
    );

    const labelColor = darkMode ? '#fff' : '#5B5B5B';

    const shelterDailyDetailsSeries = useMemo(() => {
        const series: any[] = [
            { name: 'Max', data: [] },
            { name: 'Average', data: [] },
            { name: 'Min', data: [] }
        ];
        shelterThresholdData.forEach((t) => {
            series.push({ name: t.name, data: [] });
        });
        shelterFillLevelData.forEach((element) => {
            series[0].data.push({ x: element.intervalStart, y: element.max });
            series[1].data.push({ x: element.intervalStart, y: element.average });
            series[2].data.push({ x: element.intervalStart, y: element.min });
            for (let i = 0; i < shelterThresholdData.length; i += 1) {
                series[3 + i].data.push({
                    x: element.intervalStart,
                    y: shelterThresholdData[i].value
                });
            }
        });
        return series;
    }, [shelterFillLevelData, shelterThresholdData]);

    const gatesOpeningsSeries = useMemo(() => {
        const keys = ['openingsIn', 'openingsOut', 'panicAlarms'] as const;
        return keys.map((key) => ({
            name: key,
            data: gatesOpeningsData.map((shop) => ({ x: shop.shopName, y: shop[key] }))
        }));
    }, [gatesOpeningsData]);

    const gatesOpeningsMaxValue = useMemo(() => {
        const max = gatesOpeningsData.reduce((acc, shop) => {
            const total = shop.openingsIn + shop.openingsOut + shop.panicAlarms;
            return total > acc ? total : acc;
        }, 0);
        return max > 0 ? max : 5;
    }, [gatesOpeningsData]);

    const hybridLocDailySeries = useMemo(
        () => [
            {
                name: 'Usage',
                data: hybridLocDailyData.map((item) => ({ x: item.date, y: item.count }))
            }
        ],
        [hybridLocDailyData]
    );

    const hybridLocDistributionSeries = useMemo(() => {
        const series: any[] = [
            { name: 'App Usage', data: [] },
            { name: 'Coin Usage', data: [] }
        ];
        hybridLocDistributionData.forEach((shop) => {
            const total = shop.countApp + shop.countCoin;
            series[0].data.push({ x: shop.shopName, y: shop.countApp, total });
            series[1].data.push({ x: shop.shopName, y: shop.countCoin, total });
        });
        return series;
    }, [hybridLocDistributionData]);

    const hybridLocDistributionMaxValue = useMemo(() => {
        const max = hybridLocDistributionData.reduce((acc, shop) => {
            const total = shop.countApp + shop.countCoin;
            return total > acc ? total : acc;
        }, 0);
        return max > 0 ? max : 5;
    }, [hybridLocDistributionData]);

    const hybridLocTotalSeries = useMemo(() => {
        const totalApp = hybridLocDistributionData.reduce((acc, s) => acc + s.countApp, 0);
        const totalCoin = hybridLocDistributionData.reduce((acc, s) => acc + s.countCoin, 0);
        const grandTotal = totalApp + totalCoin;
        return [
            { name: 'App Usage', data: [{ x: 'Total', y: totalApp, total: grandTotal }] },
            { name: 'Coin Usage', data: [{ x: 'Total', y: totalCoin, total: grandTotal }] }
        ];
    }, [hybridLocDistributionData]);

    const hybridLocTotalMaxValue = useMemo(
        () => hybridLocDistributionData.reduce((acc, s) => acc + s.countApp + s.countCoin, 0),
        [hybridLocDistributionData]
    );

    const dataLabelsFormatter = (value: number, opts: any) => {
        const { total } = opts.w.config.series[opts.seriesIndex]?.data[opts.dataPointIndex] || {
            total: 0
        };
        const percentage = total > 0 ? `${((value / total) * 100).toFixed(1)}%` : '0%';
        return [value, percentage];
    };

    return (
        <Box
            sx={{
                px: 3,
                py: 3,
                gap: 3,
                display: 'flex',
                flexDirection: 'column',
                '& .app-card-title': { paddingTop: '1.25rem' }
            }}
        >
            {/* Single Select Dropdown */}
            <AppCard heading="Single Select Dropdown">
                <Box sx={{ p: 2, pt: 3 }}>
                    <TabSelector tabs={SELECTOR_TABS} value={singleSelectorTab} onChange={setSingleSelectorTab}>
                        <Box sx={{ pt: 3 }}>
                            {singleSelectorTab === 0 && (
                                <BaseSelector
                                    value={singleEmpty}
                                    items={demoItems}
                                    label="Dropdown Label"
                                    placeholder="choose one option"
                                    onChange={setSingleEmpty}
                                />
                            )}
                            {singleSelectorTab === 1 && (
                                <BaseSelector
                                    value={singleSelected}
                                    items={demoItems}
                                    label="Dropdown Label"
                                    onChange={setSingleSelected}
                                />
                            )}
                            {singleSelectorTab === 2 && (
                                <BaseSelector
                                    value={singleDisabled}
                                    items={demoItems}
                                    label="Dropdown Label"
                                    disabled
                                    onChange={() => {}}
                                />
                            )}
                            {singleSelectorTab === 3 && (
                                <BaseSelector
                                    value={singleSearchable}
                                    items={demoItems}
                                    label="Dropdown Label"
                                    searchable
                                    placeholder="start to type or click to open"
                                    onChange={setSingleSearchable}
                                />
                            )}
                            {singleSelectorTab === 4 && (
                                <BaseSelector
                                    value={singleLargeList}
                                    items={LARGE_LIST_ITEMS}
                                    label="Dropdown Label"
                                    placeholder="choose one option"
                                    onChange={setSingleLargeList}
                                />
                            )}
                            {singleSelectorTab === 5 && (
                                <BaseSelector
                                    value={singleLargeList}
                                    items={LARGE_LIST_ITEMS}
                                    label="Dropdown Label"
                                    searchable
                                    placeholder="choose one option"
                                    onChange={setSingleLargeList}
                                />
                            )}
                            {singleSelectorTab === 6 && (
                                <BaseSelector
                                    value={singleSelected}
                                    items={demoItems}
                                    label="Dropdown Label"
                                    error
                                    helperText={'Description about the error'}
                                    onChange={setSingleSelected}
                                />
                            )}
                            {singleSelectorTab === 7 && (
                                <BaseSelector
                                    value={singleSelected}
                                    items={demoItems}
                                    label="Dropdown Label"
                                    warning
                                    warningText="Description about the dropdown"
                                    onChange={setSingleSelected}
                                />
                            )}
                        </Box>
                    </TabSelector>
                </Box>
            </AppCard>

            {/* Multi Select Dropdown */}
            <AppCard heading="Multi Select Dropdown">
                <Box sx={{ p: 2, pt: 3 }}>
                    <TabSelector tabs={SELECTOR_TABS} value={multiSelectorTab} onChange={setMultiSelectorTab}>
                        <Box sx={{ pt: 3 }}>
                            {multiSelectorTab === 0 && (
                                <BaseSelector
                                    multiple
                                    value={multiEmpty}
                                    items={demoItems}
                                    label="Dropdown Label"
                                    placeholder="choose multiple options"
                                    onChange={setMultiEmpty}
                                />
                            )}
                            {multiSelectorTab === 1 && (
                                <BaseSelector
                                    multiple
                                    value={multiSelected}
                                    items={demoItems}
                                    label="Dropdown Label"
                                    onChange={setMultiSelected}
                                />
                            )}
                            {multiSelectorTab === 2 && (
                                <BaseSelector
                                    multiple
                                    value={multiDisabled}
                                    items={demoItems}
                                    label="Dropdown Label"
                                    disabled
                                    onChange={() => {}}
                                />
                            )}
                            {multiSelectorTab === 3 && (
                                <BaseSelector
                                    multiple
                                    value={multiSearchable}
                                    items={demoItems}
                                    label="Dropdown Label"
                                    searchable
                                    placeholder="start to type or click to open"
                                    onChange={setMultiSearchable}
                                />
                            )}
                            {multiSelectorTab === 4 && (
                                <BaseSelector
                                    multiple
                                    value={multiLargeList}
                                    items={LARGE_LIST_ITEMS}
                                    label="Dropdown Label"
                                    placeholder="choose multiple options"
                                    onChange={setMultiLargeList}
                                />
                            )}
                            {multiSelectorTab === 5 && (
                                <BaseSelector
                                    multiple
                                    value={multiLargeList}
                                    items={LARGE_LIST_ITEMS}
                                    label="Dropdown Label"
                                    searchable
                                    placeholder="start to type or click to open"
                                    onChange={setMultiLargeList}
                                />
                            )}
                            {multiSelectorTab === 6 && (
                                <BaseSelector
                                    multiple
                                    value={multiSelected}
                                    items={demoItems}
                                    label="Dropdown Label"
                                    error
                                    helperText={'Description about the error'}
                                    onChange={setMultiSelected}
                                />
                            )}
                            {multiSelectorTab === 7 && (
                                <BaseSelector
                                    multiple
                                    value={multiSelected}
                                    items={demoItems}
                                    label="Dropdown Label"
                                    warning
                                    warningText="Description about the dropdown"
                                    onChange={setMultiSelected}
                                />
                            )}
                        </Box>
                    </TabSelector>
                </Box>
            </AppCard>

            {/* Calendar Selection */}
            <AppCard heading="Calendar Selection">
                <Box sx={{ p: 2, pt: 3 }}>
                    <TabSelector tabs={CALENDAR_TABS} value={calendarTab} onChange={setCalendarTab}>
                        <Box sx={{ pt: 3 }}>
                            {calendarTab === 0 && (
                                <DateSelector
                                    value={calendarEmpty}
                                    onChange={setCalendarEmpty}
                                    label="Date Label"
                                    placeholder="select date"
                                />
                            )}
                            {calendarTab === 1 && (
                                <DateSelector
                                    value={calendarSelected}
                                    onChange={setCalendarSelected}
                                    label="Date Label"
                                />
                            )}
                            {calendarTab === 2 && (
                                <DateSelector
                                    value={calendarSelected}
                                    onChange={() => {}}
                                    label="Date Label"
                                    disabled
                                />
                            )}
                        </Box>
                    </TabSelector>
                </Box>
            </AppCard>

            {/* Time Selection */}
            <AppCard heading="Time Selection">
                <Box sx={{ p: 2, pt: 3 }}>
                    <TabSelector tabs={CALENDAR_TABS} value={timeSelectorTab} onChange={setTimeSelectorTab}>
                        <Box sx={{ pt: 3 }}>
                            {timeSelectorTab === 0 && (
                                <TimeSelector value={timeEmpty} onChange={setTimeEmpty} label="Time Label" />
                            )}
                            {timeSelectorTab === 1 && (
                                <TimeSelector value={timeSelected} onChange={setTimeSelected} label="Time Label" />
                            )}
                            {timeSelectorTab === 2 && (
                                <TimeSelector value={timeSelected} onChange={() => {}} label="Time Label" disabled />
                            )}
                        </Box>
                    </TabSelector>
                </Box>
            </AppCard>

            {/* Buttons */}
            <AppCard heading="Buttons">
                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', mb: 2 }}>
                        <Box sx={{ width: 140 }} />
                        <Box sx={{ width: 174, textAlign: 'center' }}>
                            <Typography
                                variant="subtitle2"
                                sx={{ color: labelColor, fontWeight: 700, letterSpacing: 1 }}
                            >
                                PRIMARY
                            </Typography>
                        </Box>
                        <Box sx={{ width: 40 }} />
                        <Box sx={{ width: 174, textAlign: 'center' }}>
                            <Typography
                                variant="subtitle2"
                                sx={{ color: labelColor, fontWeight: 700, letterSpacing: 1 }}
                            >
                                RED BUTTON
                            </Typography>
                        </Box>
                    </Box>

                    {BUTTON_ROWS.map((row) => (
                        <Box key={row.label} sx={{ mb: 2.5 }}>
                            <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
                                <Box sx={{ width: 140 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: labelColor,
                                            fontWeight: 700,
                                            letterSpacing: 1
                                        }}
                                    >
                                        {row.label}
                                    </Typography>
                                </Box>
                                <Box sx={{ width: 174 }}>
                                    <WcnButton variant="primary" disabled={row.disabled} isProgress={row.isProgress}>
                                        SAVE
                                    </WcnButton>
                                </Box>
                                <Box sx={{ width: 40 }} />
                                <Box sx={{ width: 174 }}>
                                    <WcnButton variant="red" disabled={row.disabled} isProgress={row.isProgress}>
                                        CANCEL
                                    </WcnButton>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    display: { xs: 'flex', sm: 'none' },
                                    flexDirection: 'column',
                                    gap: 1.5
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{ color: labelColor, fontWeight: 700, letterSpacing: 1 }}
                                >
                                    {row.label}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1,
                                        alignItems: 'flex-start'
                                    }}
                                >
                                    <WcnButton variant="primary" disabled={row.disabled} isProgress={row.isProgress}>
                                        SAVE
                                    </WcnButton>
                                    <WcnButton variant="red" disabled={row.disabled} isProgress={row.isProgress}>
                                        CANCEL
                                    </WcnButton>
                                </Box>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </AppCard>

            {/* Tab Selector */}
            <AppCard heading="Tab Selector">
                <Box sx={{ p: 2 }}>
                    <TabSelector
                        tabs={[
                            { key: 'Overview', label: 'Overview' },
                            { key: 'Details', label: 'Details' },
                            { key: 'History', label: 'History' },
                            { key: 'Settings', label: 'Settings' }
                        ]}
                        value={activeTab}
                        onChange={setActiveTab}
                    >
                        {activeTab === 0 && (
                            <Typography sx={{ color: darkMode ? '#fff' : '#5B5B5B' }}>
                                You are viewing Tab 1 — Overview. This is where you see general information.
                            </Typography>
                        )}
                        {activeTab === 1 && (
                            <Typography sx={{ color: darkMode ? '#fff' : '#5B5B5B' }}>
                                You are viewing Tab 2 — Details. Detailed data and specifics are shown here.
                            </Typography>
                        )}
                        {activeTab === 2 && (
                            <Typography sx={{ color: darkMode ? '#fff' : '#5B5B5B' }}>
                                You are viewing Tab 3 — History. Past records and activity logs live here.
                            </Typography>
                        )}
                        {activeTab === 3 && (
                            <Typography sx={{ color: darkMode ? '#fff' : '#5B5B5B' }}>
                                You are viewing Tab 4 — Settings. Configuration options are available here.
                            </Typography>
                        )}
                    </TabSelector>
                </Box>
            </AppCard>

            <AppCard heading="Table - List Version">
                <Box className="demo-section">
                    <NewWcnTable
                        columns={columns}
                        data={trolley}
                        size="small"
                        pagination={{ count: trolley.length, rowsPerPageOptions: [5, 10, 15] }}
                    />
                </Box>
            </AppCard>

            <AppCard heading="Table - Selectable">
                <Box className="demo-section">
                    <NewWcnTable
                        columns={columns}
                        data={trolley}
                        size="small"
                        selection={{
                            selectedItems: selectedTrolleys,
                            onSelectionChange: setSelectedTrolleys,
                            getItemKey: (item) => item.groupName,
                            selectionMode: 'multiple'
                        }}
                        pagination={{ count: trolley.length, rowsPerPageOptions: [5, 10, 15] }}
                    />
                </Box>
            </AppCard>

            <AppCard heading="Table - Multiple lines in one row">
                <Box className="demo-section">
                    <NewWcnTable
                        columns={columnsWithActions}
                        data={trolley}
                        size="small"
                        pagination={{ count: trolley.length, rowsPerPageOptions: [5, 10, 15] }}
                    />
                </Box>
            </AppCard>

            <AppCard heading="Tags">
                <Box className="demo-section" sx={{ p: 2 }}>
                    <Grid container spacing={4}>
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ color: darkMode ? '#FFF' : '#333', mb: 2 }}>
                                Health Status Tags
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                <StatusTag config={HealthStatus.Healthy} />
                                <StatusTag config={HealthStatus.Check} />
                                <StatusTag config={HealthStatus.UnHealthy} />
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ color: darkMode ? '#FFF' : '#333', mb: 2 }}>
                                Shop Status Tags
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                <StatusTag config={ShopStatus.Closed} />
                                <StatusTag config={ShopStatus.Out_Of_Service} />
                                <StatusTag config={ShopStatus.Unmanned} />
                                <StatusTag config={ShopStatus.Staffed} />
                                <StatusTag config={ShopStatus.Hybrid} />
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <EmailInput
                                recipientEmails={userEmails}
                                initialEmails={emails}
                                onUpdate={setEmails}
                                label="Email Recipients"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <EmailInput
                                recipientEmails={userEmails}
                                initialEmails={emails}
                                onUpdate={setEmails}
                                label="Warning Email Recipients"
                                onChipWarning={isUnknowEmail}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </AppCard>

            <AppCard heading="Trolley Shelter Daily Details - Line Chart with Thresholds">
                <Box className="demo-section" sx={{ p: 0.5, backgroundColor: darkMode ? '#262626' : '#f5f5f5' }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ color: darkMode ? '#FFF' : '#666', mb: 2 }}>
                        Fill Level History with Max/Average/Min and Threshold Lines
                    </Typography>
                    <WcnApexchart
                        chartId="shelter-daily-details-chart"
                        type="line"
                        series={shelterDailyDetailsSeries}
                        height={480}
                        xAxisType="datetime"
                        strokeDashArray={[0, 0, 0, 10, 10, 10]}
                        strokeCurve="straight"
                        tooltipShared={true}
                    />
                </Box>
            </AppCard>

            <AppCard heading="Gates Openings - Stacked Bar Chart">
                <Box className="demo-section" sx={{ p: 0.5, backgroundColor: darkMode ? '#262626' : '#f5f5f5' }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ color: darkMode ? '#FFF' : '#666', mb: 2 }}>
                        Gate Activity by Type Across Stores
                    </Typography>
                    <WcnApexchart
                        chartId="gates-openings-chart"
                        type="bar"
                        series={gatesOpeningsSeries}
                        height={550}
                        stacked={true}
                        dataLabelsEnabled={true}
                        xAxisType="category"
                        yAxisMax={gatesOpeningsMaxValue}
                        yAxisMin={0}
                    />
                </Box>
            </AppCard>

            <AppCard heading="HybridLoc Daily Usage - Simple Line Chart">
                <Box className="demo-section" sx={{ p: 0.5, backgroundColor: darkMode ? '#262626' : '#f5f5f5' }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ color: darkMode ? '#FFF' : '#666', mb: 2 }}>
                        Daily HybridLoc Usage Trend
                    </Typography>
                    <WcnApexchart
                        chartId="hybridloc-daily-chart"
                        type="line"
                        series={hybridLocDailySeries}
                        height={550}
                        xAxisType="datetime"
                        yAxisMin={0}
                        strokeCurve="straight"
                        strokeDashArray={[0]}
                        tooltipShared={false}
                    />
                </Box>
            </AppCard>

            <AppCard heading="HybridLoc Distribution Usage - Stacked Bar with Total">
                <Box className="demo-section" sx={{ p: 0.5, backgroundColor: darkMode ? '#262626' : '#f5f5f5' }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ color: darkMode ? '#FFF' : '#666', mb: 2 }}>
                        App vs Coin Usage Distribution by Store
                    </Typography>
                    <WcnApexchart
                        chartId="hybridloc-distribution-main"
                        type="bar"
                        series={hybridLocDistributionSeries}
                        height={550}
                        stacked={true}
                        xAxisType="category"
                        yAxisMin={0}
                        yAxisMax={hybridLocDistributionMaxValue}
                        dataLabelsEnabled={true}
                        dataLabelsFormatter={dataLabelsFormatter}
                        tooltipShared={false}
                        dataLabelsOffsetY={20}
                    />
                    <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ color: darkMode ? '#FFF' : '#666', mb: 2, mt: 3 }}
                    >
                        Total Combined Usage (Horizontal)
                    </Typography>
                    <WcnApexchart
                        chartId="hybridloc-distribution-total"
                        type="bar"
                        series={hybridLocTotalSeries}
                        height={150}
                        stacked={true}
                        horizontal={true}
                        xAxisType="category"
                        xAxisCategories={['Total']}
                        xAxisMin={0}
                        xAxisMax={hybridLocTotalMaxValue}
                        yAxisMin={0}
                        hideYAxisLabels={isMobile}
                        dataLabelsEnabled={true}
                        dataLabelsFormatter={dataLabelsFormatter}
                        tooltipShared={false}
                    />
                </Box>
            </AppCard>

            <AppCard heading="Actions">
                <Box sx={{ px: 2, pt: 2 }}>
                    <WcnCheckbox
                        checked={actionsDisabled}
                        onChange={(e) => setActionsDisabled(e.target.checked)}
                        size="small"
                        label="Disable"
                    />
                </Box>

                <Box
                    sx={{
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                        alignItems: 'flex-start'
                    }}
                >
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                            Actions
                        </Typography>
                    </Box>
                    <Box>
                        <IconButton size="small" disabled={actionsDisabled}>
                            <i className="bi bi-eye" />
                        </IconButton>
                        <IconButton size="small" disabled={actionsDisabled}>
                            <i className="bi bi-pencil" />
                        </IconButton>
                        <IconButton size="small" disabled={actionsDisabled}>
                            <i className="bi bi-play-circle" />
                        </IconButton>
                        <IconButton size="small" disabled={actionsDisabled}>
                            <i className="bi bi-trash3" />
                        </IconButton>
                    </Box>
                </Box>
                <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                        Radio Item Group
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Box>
                            <RadioItemGroup
                                options={SELECTION_OPTIONS}
                                value={radioSelection}
                                onChange={setRadioSelection}
                                disabled={actionsDisabled}
                            />
                        </Box>
                    </Box>
                </Box>
                <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                        Checkbox
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <WcnCheckbox
                            label="Checkbox selected"
                            checked={checkboxSelected}
                            disabled={actionsDisabled}
                            onChange={(e) => setCheckboxSelected(e.target.checked)}
                        />
                        <WcnCheckbox
                            checked={checkboxSelectable}
                            label="Checkbox selectable"
                            disabled={actionsDisabled}
                            onChange={(e) => setCheckboxSelectable(e.target.checked)}
                        />
                    </Box>
                </Box>

                <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                        Toggle (sets loading state on second toggle)
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <ToggleButton
                            checked={toggleButtonSelected}
                            onChange={() => setToggleButtonSelected(!toggleButtonSelected)}
                            loading={toggleButton2Selected}
                            disabled={actionsDisabled}
                        />
                        <ToggleButton
                            checked={toggleButton2Selected}
                            onChange={() => setToggleButton2Selected(!toggleButton2Selected)}
                            loading={toggleButtonSelected}
                            disabled={actionsDisabled}
                        />
                    </Box>
                </Box>
            </AppCard>

            <AppCard heading="Input">
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Box sx={{ px: 2, pt: 2, gap: 2 }}>
                            <WcnTextField label="normal input" sx={{ width: 386 }} />
                        </Box>
                        <Box sx={{ px: 2, pt: 2, gap: 2 }}>
                            <WcnTextField
                                label="warning input"
                                warning
                                warningText="description about the input"
                                sx={{ width: 386 }}
                            />
                        </Box>
                        <Box sx={{ px: 2, pt: 2, gap: 2 }}>
                            <WcnTextField
                                error
                                label="error input"
                                helperText="description about the error"
                                sx={{ width: 386 }}
                            />
                        </Box>
                        <Box sx={{ px: 2, pt: 2, gap: 2 }}>
                            <WcnTextField label="disable input" value="input" disabled sx={{ width: 386 }} />
                        </Box>
                    </Grid>
                </Grid>
            </AppCard>
        </Box>
    );
};

export default DesignElements;
